import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import Modal from "../../../components/Modal";
import AdminButton from "../action_engine/AdminButton";

export default function AdminPromptDialog({
  request,
  onSubmit,
  onCancel,
}) {
  const inputRef = useRef(null);
  const [value, setValue] = useState("");
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (!request) {
      setValue("");
      setValidationError("");
      return;
    }

    const initialValue =
      request.options?.defaultValue == null
        ? ""
        : String(request.options.defaultValue);

    setValue(initialValue);
    setValidationError("");
  }, [request]);

  if (!request) {
    return null;
  }

  const {
    title = "Saisie requise",
    message = "",
    label = "Valeur",
    placeholder = "",
    confirmLabel = "Valider",
    cancelLabel = "Annuler",
    maxLength,
    required = false,
    validator,
  } = request.options || {};

  const titleId = `admin-prompt-title-${request.id}`;
  const messageId = `admin-prompt-message-${request.id}`;
  const inputId = `admin-prompt-input-${request.id}`;
  const errorId = `admin-prompt-error-${request.id}`;

  const validate = () => {
    if (required && !value.trim()) {
      return "Cette valeur est obligatoire.";
    }

    if (typeof validator === "function") {
      const result = validator(value);

      if (result === false) {
        return "La valeur saisie est invalide.";
      }

      if (typeof result === "string") {
        return result;
      }
    }

    return "";
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const error = validate();

    if (error) {
      setValidationError(error);
      inputRef.current?.focus();
      return;
    }

    onSubmit(value);
  };

  const describedBy = [
    message ? messageId : null,
    validationError ? errorId : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Modal
      onClose={onCancel}
      role="dialog"
      ariaLabelledBy={titleId}
      ariaDescribedBy={describedBy || undefined}
      initialFocusRef={inputRef}
    >
      <PromptForm onSubmit={handleSubmit}>
        <PromptTitle id={titleId}>{title}</PromptTitle>

        {message && <PromptMessage id={messageId}>{message}</PromptMessage>}

        <FieldGroup>
          <FieldLabel htmlFor={inputId}>{label}</FieldLabel>

          <PromptInput
            ref={inputRef}
            id={inputId}
            type="text"
            value={value}
            placeholder={placeholder}
            maxLength={maxLength}
            aria-invalid={Boolean(validationError)}
            aria-describedby={validationError ? errorId : undefined}
            onChange={(event) => {
              setValue(event.target.value);

              if (validationError) {
                setValidationError("");
              }
            }}
          />

          {validationError && (
            <ValidationError id={errorId} role="alert">
              {validationError}
            </ValidationError>
          )}
        </FieldGroup>

        <Actions>
          <CancelButton
            type="button"
            variant="ghost"
            onClick={onCancel}
          >
            {cancelLabel}
          </CancelButton>

          <SubmitButton
            type="submit"
            variant="primary"
          >
            {confirmLabel}
          </SubmitButton>
        </Actions>
      </PromptForm>
    </Modal>
  );
}

const PromptForm = styled.form`
  width: min(480px, 100%);
`;

const PromptTitle = styled.h2`
  margin: 0 2.5rem 1rem 0;
  color: ${({ theme }) => theme?.colors?.text || "#f7f7f7"};
  font-size: 1.25rem;
  line-height: 1.3;
`;

const PromptMessage = styled.p`
  margin: 0 0 1rem;
  color: ${({ theme }) => theme?.colors?.text || "#f7f7f7"};
  font-size: 0.98rem;
  line-height: 1.55;
`;

const FieldGroup = styled.div`
  display: grid;
  gap: 0.45rem;
`;

const FieldLabel = styled.label`
  color: ${({ theme }) => theme?.colors?.text || "#f7f7f7"};
  font-size: 0.9rem;
  font-weight: 700;
`;

const PromptInput = styled.input`
  width: 100%;
  min-height: 42px;
  box-sizing: border-box;
  padding: 0.65rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  color: ${({ theme }) => theme?.colors?.text || "#f7f7f7"};
  font: inherit;

  &::placeholder {
    color: rgba(247, 247, 247, 0.62);
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }

  &[aria-invalid="true"] {
    border-color: #ff9f9f;
  }
`;

const ValidationError = styled.p`
  margin: 0;
  color: #ffb4b4;
  font-size: 0.85rem;
  line-height: 1.4;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;

  @media (max-width: 520px) {
    flex-direction: column-reverse;
  }
`;

const CancelButton = styled(AdminButton)``;

const SubmitButton = styled(AdminButton)``;
